/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  ButtonHTMLAttributes,
  forwardRef,
  memo,
} from 'react';
import type {
  ComponentProps,
  ComponentType,
  ForwardedRef,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  Ref,
} from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { definition as faSpinner } from '@fortawesome/free-solid-svg-icons/faSpinner';

import { buttonColors } from './buttonColors';
import { buttonSizes } from './buttonSizes';

import {
  StyledLoader,
  StyledButton,
} from './style';
import type {
  ButtonColor,
  ButtonSize,
} from './types';

export type ButtonProps<
ButtonComponentType extends keyof JSX.IntrinsicElements | ComponentType<any>,
> = {
  /**
   * Цвет кнопки
   */
  color?: ButtonColor;
  /**
   * Размер кнопки
   */
  size?: ButtonSize;
  /**
  * Компонент корневого элемента кнопки
  */
  component?: ButtonComponentType;
  /**
   * Дополнительный props компонента кнопки при использовании кастомного component
   */
  componentProps?: ComponentProps<ButtonComponentType>;
  /**
   * Состояние загрузки
   */
  loading?: boolean;
  /**
   * Кнопка выключена
   */
  disabled?: boolean;
  /**
   * Кнопка зажата
   */
  pressed?: boolean;
  /**
   * Не отображать закругления слева
   */
  flatLeft?: boolean;
  /**
   * Не отображать закругления справа
   */
  flatRight?: boolean;
  /**
   * Кнопка состоит только из иконки
   */
  onlyIcon?: boolean;
  /**
   * Дополнительный класс корневого компонента кнопки
   */
  className?: string;
  /**
   * Обработчик нажатия на кнопку
   */
  onClick?: MouseEventHandler;

  children?: ReactNode;

  /**
   * Тип кнопки 'submit', 'reset' или 'button'
   */
  type?: ButtonHTMLAttributes<HTMLButtonElement>['type'];
};

const loadingIcon = (
  <StyledLoader>
    <FontAwesomeIcon
      icon={faSpinner}
      pulse
    />
  </StyledLoader>
);

const defaultComponentProps = {};

function ButtonInner<
RefElementType,
ButtonComponentType extends keyof JSX.IntrinsicElements | ComponentType<any> = 'button',
>(
  {
    color = buttonColors.DEFAULT,
    size = buttonSizes.DEFAULT,
    component: ButtonComponent = ('button' as ButtonComponentType),
    componentProps = (defaultComponentProps as ComponentProps<ButtonComponentType>),
    loading = false,
    disabled = false,
    pressed = false,
    onlyIcon = false,
    flatLeft = false,
    flatRight = false,
    className = '',

    children = undefined,

    onClick = undefined,

    type = undefined,
  }: ButtonProps<ButtonComponentType>,
  ref: ForwardedRef<RefElementType>,
): ReactElement {
  return (
    <StyledButton
      as={ButtonComponent}
      $color={color}
      $isFlatLeft={flatLeft}
      $isFlatRight={flatRight}
      $isLoading={loading}
      $isOnlyIcon={onlyIcon}
      $isPressed={pressed}
      $size={size}
      className={className}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      type={type}
      {...componentProps}
      ref={ref}
    >
      {
        loading && (
          loadingIcon
        )
      }

      {children}
    </StyledButton>
  );
}

export const Button = memo(forwardRef(ButtonInner)) as <
RefElementType,
ButtonComponentType extends keyof JSX.IntrinsicElements | ComponentType<any> = 'button',
>(
  props: ButtonProps<ButtonComponentType> & {
    ref?: Ref<RefElementType>;
  },
) => ReactElement;
